/**
 * Product Features Handler
 * Handles dynamic updates of product features (Spin, Tempo, Trajectory) based on variant selection
 */

class ProductFeatures extends HTMLElement {
  constructor() {
    super();
    this.variantFeatures = {};
    this.init();
  }

  /**
   * Normalize feature values from metafields
   * Handles numeric values (1, 2, 3) and text values (low, medium, high, etc.)
   */
  normalizeValue(value, type = 'spin') {
    if (!value) return '';
    
    const normalized = String(value).toLowerCase().trim();
    
    // Handle numeric values
    if (normalized === '1') {
      if (type === 'tempo') return 'deliberate';
      return 'low';
    }
    if (normalized === '2') {
      if (type === 'tempo') return 'moderate';
      return 'medium';
    }
    if (normalized === '3') {
      if (type === 'tempo') return 'fast';
      return 'high';
    }
    
    // Handle text aliases
    if (normalized === 'mid') return 'medium';
    
    // Return as-is if already normalized or unknown
    return normalized;
  }

  /**
   * Process and normalize all variant features data
   */
  processVariantFeatures(rawData) {
    const processed = {};
    
    for (const [variantId, features] of Object.entries(rawData)) {
      processed[variantId] = {
        spin: this.normalizeValue(features.spin, 'spin'),
        tempo: this.normalizeValue(features.tempo, 'tempo'),
        trajectory: this.normalizeValue(features.trajectory, 'trajectory')
      };
    }
    
    return processed;
  }

  init() {
    // Read variant features data from JSON script tag
    const dataScript = document.getElementById(`ProductFeatures-${this.dataset.sectionId}`);
    if (dataScript) {
      try {
        const rawData = JSON.parse(dataScript.textContent);
        // Normalize all values in JavaScript
        this.variantFeatures = this.processVariantFeatures(rawData);
      } catch (error) {
        console.error('Product Features: Error parsing variant features data', error);
        return;
      }
    } else {
      console.warn('Product Features: Variant features data not found');
      return;
    }

    // Listen for variant changes using the theme's pub/sub system
    if (window.webvista && window.webvista.subscribe && window.PUB_SUB_EVENTS) {
      this.variantChangeUnsubscriber = window.webvista.subscribe(
        window.PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );
    }

    // Also listen to variant input changes directly as fallback
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      this.variantInputHandler = this.handleVariantInputChange.bind(this);
      variantInput.addEventListener('change', this.variantInputHandler);
      
      // Initial update based on current variant
      if (variantInput.value) {
        this.updateFeatures(parseInt(variantInput.value));
      }
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.variantChangeUnsubscriber && window.webvista && window.webvista.unsubscribe) {
      window.webvista.unsubscribe(window.PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
    }
    
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && this.variantInputHandler) {
      variantInput.removeEventListener('change', this.variantInputHandler);
    }
  }

  handleVariantChange(event) {
    if (event && event.data && event.data.variant && event.data.variant.id) {
      this.updateFeatures(event.data.variant.id);
    }
  }

  handleVariantInputChange(event) {
    const variantId = parseInt(event.target.value);
    if (variantId) {
      this.updateFeatures(variantId);
    }
  }

  updateFeatures(variantId) {
    const features = this.variantFeatures[variantId];
    if (!features) return;

    // Update spin (first feature-item)
    const spinOptions = this.querySelectorAll('.feature-item:first-child .feature-option');
    spinOptions.forEach(option => {
      const value = option.dataset.value;
      if (features.spin === value) {
        option.classList.add('active');
        option.setAttribute('aria-pressed', 'true');
      } else {
        option.classList.remove('active');
        option.setAttribute('aria-pressed', 'false');
      }
    });

    // Update tempo (second feature-item)
    const tempoOptions = this.querySelectorAll('.feature-item:nth-child(2) .feature-option');
    tempoOptions.forEach(option => {
      const value = option.dataset.value;
      if (features.tempo === value) {
        option.classList.add('active');
        option.setAttribute('aria-pressed', 'true');
      } else {
        option.classList.remove('active');
        option.setAttribute('aria-pressed', 'false');
      }
    });

    // Update trajectory (third feature-item)
    const trajectoryOptions = this.querySelectorAll('.feature-item:nth-child(3) .feature-option');
    trajectoryOptions.forEach(option => {
      const value = option.dataset.value;
      if (features.trajectory === value) {
        option.classList.add('active');
        option.setAttribute('aria-pressed', 'true');
      } else {
        option.classList.remove('active');
        option.setAttribute('aria-pressed', 'false');
      }
    });
  }
}

// Register the custom element
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('product-features', ProductFeatures);
  });
} else {
  customElements.define('product-features', ProductFeatures);
}
